/**
 * OdinoChat — the reusable AI builder agent surface (ADR-0024, S2).
 *
 * The whole companion: the session/turn/SSE lifecycle + the structured turn
 * rendering (prose segments and tool-activity chips "🔧 get_catalog ✓", built by
 * the pure `lib/odinoTurn` reducer) + per-user history + the thinking toggle.
 * Odino is an embedded ASSISTANT, not a standalone chat page: it lives ONLY in the
 * docked right-rail (`OdinoRail`, S2.2) — there is no `/odino` page. Provider
 * settings are admin-level and live on their own page (`/settings`).
 * Fills its container (`h-full`); the host frames it. A `Cancel` button stops an
 * in-flight turn (backend `/cancel` → the orchestrator emits `cancelled`).
 */
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Bot,
  Check,
  History,
  Loader2,
  Plus,
  Search,
  Send,
  Sparkles,
  Square,
  Trash2,
  Wrench,
  X,
} from "lucide-react";

import { type OdinoSessionSummary, type OdinoSettings, odinoApi } from "@/lib/api-odino";
import { deriveOdinoContext } from "@/lib/odinoContext";
import { filterSessions, groupSessionsByDate, transcriptToTurns } from "@/lib/odinoHistory";
import { REVEAL_TOOLS, revealFromNavigate, useOdinoBuild } from "@/lib/odinoBuild";
import { invalidateBuilderQueries, isMutatingToolSuccess } from "@/lib/odinoRefresh";
import { type OdinoWireEvent, type TurnItem, applyEvent, shouldNavigateTo } from "@/lib/odinoTurn";
import { ChatMarkdown } from "@/components/ui/ChatMarkdown";
import { useAuthStore } from "@/lib/auth";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  text?: string; // user message body
  items?: TurnItem[]; // assistant turn — interleaved text segments + tool chips
  streaming?: boolean;
  error?: boolean;
}

let _seq = 0;
const nextId = () => `m${++_seq}`;

export function OdinoChat({
  embedded = false,
  onBusyChange,
}: { embedded?: boolean; onBusyChange?: (busy: boolean) => void } = {}) {
  const token = useAuthStore((s) => s.token);
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  // Live pathname, read inside the SSE handler so a navigate decision compares
  // against where the operator IS now — not the path captured when the stream
  // opened (which goes stale the moment Odino navigates them somewhere).
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const [settings, setSettings] = useState<OdinoSettings | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  // null = not yet loaded (spinner); [] = loaded-empty. Per-user (backend scopes
  // the list to the caller), so this only ever shows the operator's OWN history.
  const [sessions, setSessions] = useState<OdinoSessionSummary[] | null>(null);
  // Extended thinking opt-in (Claude-style toggle), OFF by default; the choice
  // sticks across reloads. Passed per turn (the orchestrator gates it per turn).
  const [thinking, setThinking] = useState(
    () => localStorage.getItem("tray:odino:thinking") === "1",
  );
  useEffect(() => {
    localStorage.setItem("tray:odino:thinking", thinking ? "1" : "0");
  }, [thinking]);
  const esRef = useRef<EventSource | null>(null);
  const assistantIdRef = useRef<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // Load settings + open a session once. Surface failures (instead of swallowing
  // them) so a broken setup is visible rather than presenting as silently dead
  // buttons — the failure mode that hid the api-odino path-prefix bug.
  useEffect(() => {
    void odinoApi
      .getSettings()
      .then(setSettings)
      .catch(() => {
        setSettings(null);
        setLoadError("Impossibile caricare le impostazioni di Odino.");
      });
    void odinoApi
      .createSession()
      .then((s) => setSessionId(s.id))
      .catch(() => setLoadError("Impossibile avviare una sessione Odino."));
  }, []);

  // Open the SSE stream for the session and route events into the live message.
  useEffect(() => {
    if (!sessionId || !token) return;
    const es = new EventSource(odinoApi.streamUrl(sessionId, token));
    esRef.current = es;

    // Fold a wire event into the streaming assistant turn's items (text
    // segments + tool chips), so the agent's work renders legibly instead of as
    // one run-on blob.
    const fold = (ev: OdinoWireEvent) =>
      setMessages((ms) =>
        ms.map((m) =>
          m.id === assistantIdRef.current ? { ...m, items: applyEvent(m.items ?? [], ev) } : m,
        ),
      );
    const finish = (patch: Partial<ChatMessage> = {}) => {
      // Snapshot the id BEFORE clearing the ref: setMessages' updater runs
      // asynchronously, so if it read assistantIdRef.current it would see the
      // null we set on the next line and match nothing — leaving `streaming`
      // (the spinner) stuck on. Close over the captured id instead.
      const id = assistantIdRef.current;
      assistantIdRef.current = null;
      setBusy(false);
      setMessages((ms) => ms.map((m) => (m.id === id ? { ...m, streaming: false, ...patch } : m)));
    };
    const parse = (e: MessageEvent) => {
      try {
        return JSON.parse(e.data);
      } catch {
        return {};
      }
    };

    const onToken = (e: MessageEvent) => fold({ type: "token", text: parse(e).text });
    const onTool = (e: MessageEvent) => fold({ type: "tool", name: parse(e).name });
    const onToolResult = (e: MessageEvent) => {
      const d = parse(e);
      fold({
        type: "tool_result",
        name: d.name,
        is_error: d.is_error,
        requires_confirmation: d.requires_confirmation,
        confirm: d.confirm,
      });
      // Live-build refresh (S3.3): Odino wrote app structure through its own
      // backend session, so the open builder's caches are stale. A successful
      // commit_draft / apply_patch invalidates the builder query families so the
      // page/table/column appears beside the rail without a manual reload.
      if (isMutatingToolSuccess(d.name, d.is_error)) invalidateBuilderQueries(queryClient);
      // Draft-first flow builds: a successful commit_workflow/commit_etl arms
      // the REVEAL — the matching editor auto-loads the fresh draft and
      // stagger-animates the graph in. Armed BEFORE the navigate below so the
      // editor mounts with the signal already set.
      if (d.name && REVEAL_TOOLS.has(d.name) && !d.is_error) {
        const reveal = revealFromNavigate(d.navigate);
        if (reveal) useOdinoBuild.getState().setReveal({ ...reveal, armedAt: Date.now() });
      }
      // Take the operator to the area Odino just changed (slice D), so they
      // watch it land live. The orchestrator only sets `navigate` on a
      // successful structural write, and to a vetted / path. Skip if
      // they're already there (avoids a redundant route push).
      if (shouldNavigateTo(d.navigate, pathRef.current)) navigate(d.navigate);
    };
    const onDone = () => finish();
    const onError = (e: MessageEvent) => {
      // Capture the id up front: appending the error text AND clearing the ref
      // must happen against the same message, in one updater (the ref is nulled
      // synchronously, before an async updater would read it).
      const id = assistantIdRef.current;
      const msg = parse(e).message ?? "Errore";
      assistantIdRef.current = null;
      setBusy(false);
      setMessages((ms) =>
        ms.map((m) =>
          m.id === id
            ? {
                ...m,
                streaming: false,
                error: true,
                items: applyEvent(m.items ?? [], {
                  type: "token",
                  text: ((m.items ?? []).length ? "\n\n" : "") + msg,
                }),
              }
            : m,
        ),
      );
    };
    es.addEventListener("token", onToken as EventListener);
    es.addEventListener("tool", onTool as EventListener);
    es.addEventListener("tool_result", onToolResult as EventListener);
    es.addEventListener("done", onDone as EventListener);
    es.addEventListener("cancelled", onDone as EventListener);
    es.addEventListener("error", onError as EventListener);
    return () => {
      es.close();
      esRef.current = null;
    };
  }, [sessionId, token, queryClient, navigate]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  // Auto-grow the composer like Claude's: reset to content height each keystroke,
  // capped at ~10rem (matches max-h-40), beyond which it scrolls. Keyed on `input`
  // so it also shrinks back to one line after send clears it.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [input]);

  // Lift the in-flight state so the host (the rail) can lock the builder while
  // Odino works (slice D — "bloccare l'editor" during a live build).
  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);

  const sendText = useCallback(
    async (raw: string) => {
      const text = raw.trim();
      if (!text || busy) return;
      const aId = nextId();
      assistantIdRef.current = aId;
      setMessages((ms) => [
        ...ms,
        { id: nextId(), role: "user", text },
        { id: aId, role: "assistant", items: [], streaming: true },
      ]);
      setBusy(true);
      try {
        // Self-heal the session: Odino S0 keeps sessions in memory, so a backend
        // restart (or expiry) drops the one we hold. Ensure a session, and if the
        // turn POST fails (likely the session vanished), recreate ONCE and retry —
        // changing sessionId reconnects the SSE stream to the fresh session, whose
        // queue buffers events so nothing is lost.
        // Tell Odino where the operator currently is (slice A), so it resolves
        // "this app / this page" without asking for the slug.
        const context = deriveOdinoContext(location.pathname);
        let sid = sessionId ?? (await odinoApi.createSession()).id;
        if (sid !== sessionId) setSessionId(sid);
        try {
          await odinoApi.postTurn(sid, text, context, thinking);
        } catch {
          sid = (await odinoApi.createSession()).id;
          setSessionId(sid);
          await odinoApi.postTurn(sid, text, context, thinking);
        }
      } catch {
        setMessages((ms) =>
          ms.map((m) =>
            m.id === aId
              ? {
                  ...m,
                  streaming: false,
                  error: true,
                  items: [{ kind: "text", text: "Invio fallito" }],
                }
              : m,
          ),
        );
        assistantIdRef.current = null;
        setBusy(false);
      }
    },
    [sessionId, busy, location.pathname, thinking],
  );

  const send = useCallback(() => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    void sendText(text);
  }, [input, sendText]);

  // The human confirm button (B2): clicking it sends an explicit yes that drives
  // the agent to re-call the tool with confirm_destructive:true. Plain-language
  // so it works for every destructive tool (drop_column/table/page, remove
  // component) without the rail needing to know which tool is pending.
  const confirmDestructive = useCallback(() => {
    void sendText(
      "Sì, confermo: procedi ed esegui le modifiche distruttive (confirm_destructive).",
    );
  }, [sendText]);
  const cancelDestructive = useCallback(() => {
    void sendText("No, annulla: NON eseguire le modifiche distruttive. Lascia tutto invariato.");
  }, [sendText]);

  // Stop an in-flight turn: the backend flips the session's cancel flag and the
  // orchestrator emits `cancelled` at the next step, which finalises the bubble.
  const cancel = useCallback(() => {
    if (sessionId) void odinoApi.cancel(sessionId).catch(() => {});
  }, [sessionId]);

  // --- History (per-user reopenable conversations) ---------------------------
  // Open the panel + (re)load the list each time, so titles/order are fresh
  // (a finished turn just renamed/bumped a session).
  const openHistory = useCallback(() => {
    setShowHistory(true);
    setSessions(null);
    void odinoApi
      .listSessions()
      .then(setSessions)
      .catch(() => setSessions([]));
  }, []);

  // Start a brand-new conversation: a fresh empty session (no DB row until its
  // first turn) and a cleared transcript.
  const newChat = useCallback(() => {
    if (busy) return;
    setShowHistory(false);
    setMessages([]);
    assistantIdRef.current = null;
    void odinoApi
      .createSession()
      .then((s) => setSessionId(s.id))
      .catch(() => setLoadError("Impossibile avviare una sessione Odino."));
  }, [busy]);

  // Reopen a past conversation: point the session (the SSE effect reconnects to
  // its queue) and hydrate the transcript into display turns.
  const reopen = useCallback(
    (id: string) => {
      if (busy) return;
      setShowHistory(false);
      setSessionId(id);
      setMessages([]);
      assistantIdRef.current = null;
      void odinoApi
        .getSessionMessages(id)
        .then((rows) =>
          setMessages(
            transcriptToTurns(rows).map((t) => ({
              id: nextId(),
              role: t.role,
              text: t.text,
              items: t.items,
            })),
          ),
        )
        .catch(() => setLoadError("Impossibile aprire la conversazione."));
    },
    [busy],
  );

  const removeSession = useCallback(
    (id: string) => {
      void odinoApi
        .deleteSession(id)
        .then(() => {
          setSessions((ss) => (ss ?? []).filter((s) => s.id !== id));
          if (id === sessionId) newChat(); // deleted the open one → start fresh
        })
        .catch(() => {});
    },
    [sessionId, newChat],
  );

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-2 border-b border-border px-4 py-3">
        {!embedded && (
          <>
            <Bot className="h-5 w-5 text-accent" strokeWidth={1.5} />
            <h1 className="text-lg font-semibold">Odino</h1>
            <span className="text-sm text-muted-foreground">— il tuo compagno di build</span>
          </>
        )}
        <div className="ml-auto flex items-center gap-2">
          {settings && (
            <span
              className={`rounded-full px-2 py-0.5 text-xs ${
                settings.configured ? "bg-success/15 text-success" : "bg-warning/15 text-warning"
              }`}
            >
              {settings.configured
                ? `${settings.provider} · ${settings.model}`
                : "Echo (non configurato)"}
            </span>
          )}
          <button
            type="button"
            onClick={newChat}
            disabled={busy}
            title="Nuova conversazione"
            aria-label="Nuova conversazione"
            className="flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-sm text-muted-foreground hover:bg-surface hover:text-foreground disabled:opacity-50"
          >
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            {!embedded && "Nuova"}
          </button>
          <button
            type="button"
            onClick={() => (showHistory ? setShowHistory(false) : openHistory())}
            title="Cronologia conversazioni"
            aria-label="Cronologia conversazioni"
            className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm hover:bg-surface hover:text-foreground ${
              showHistory ? "border-accent text-foreground" : "border-border text-muted-foreground"
            }`}
          >
            <History className="h-4 w-4" strokeWidth={1.5} />
            {!embedded && "Cronologia"}
          </button>
          {/* Odino settings are admin-level — they live ONLY on the dedicated
              admin page (Amministrazione → Impostazioni Odino), never in the chat. */}
        </div>
      </header>

      {loadError && (
        <div className="border-b border-danger/40 bg-danger/10 px-4 py-2 text-sm text-danger">
          {loadError}
        </div>
      )}

      {showHistory ? (
        <HistoryPanel
          sessions={sessions}
          activeId={sessionId}
          onReopen={reopen}
          onDelete={removeSession}
        />
      ) : (
        <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4">
          {messages.length === 0 && (
            <p className="mt-10 text-center text-sm text-muted-foreground">
              Chiedi a Odino di costruire qualcosa. (In S0 risponde in eco se non hai configurato
              una chiave.)
            </p>
          )}
          {messages.map((m) =>
            m.role === "user" ? (
              <div key={m.id} className="flex justify-end">
                <div className="max-w-[80%] whitespace-pre-wrap rounded-lg bg-accent px-3 py-2 text-sm text-accent-foreground">
                  {m.text}
                </div>
              </div>
            ) : (
              <div key={m.id} className="flex justify-start">
                <div
                  className={`flex max-w-[85%] flex-col items-start gap-1.5 rounded-lg border px-3 py-2 text-sm ${
                    m.error ? "border-danger/40 bg-danger/10" : "border-border bg-surface"
                  }`}
                >
                  {(m.items ?? []).length === 0 && m.streaming && (
                    <span className="text-muted-foreground">
                      …<Loader2 className="ml-1 inline h-3 w-3 animate-spin" />
                    </span>
                  )}
                  {/* Prose + tool chips render in stream order; confirm cards are
                    pulled to the BOTTOM so the agent's question reads first and the
                    action sits below it. */}
                  {(m.items ?? []).map((it, i) =>
                    it.kind === "text" ? (
                      // Markdown, not raw tokens: the model formats with
                      // **bold**/lists and points at the editor with a RELATIVE
                      // link — which must SPA-navigate (a new tab / full load
                      // would drop the rail), hence onInternalNavigate.
                      <ChatMarkdown
                        key={i}
                        text={it.text}
                        className={`w-full text-sm ${m.error ? "text-danger" : "text-foreground"}`}
                        onInternalNavigate={(path) => {
                          if (shouldNavigateTo(path, location.pathname)) navigate(path);
                        }}
                      />
                    ) : it.kind === "tool" ? (
                      <ToolChip key={i} name={it.name} status={it.status} />
                    ) : null,
                  )}
                  {(m.items ?? []).map((it, i) =>
                    it.kind === "confirm" ? (
                      <ConfirmCard
                        key={`c${i}`}
                        message={it.message}
                        items={it.items}
                        busy={busy}
                        onConfirm={confirmDestructive}
                        onCancel={cancelDestructive}
                      />
                    ) : null,
                  )}
                  {m.streaming && (m.items ?? []).length > 0 && (
                    <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                  )}
                </div>
              </div>
            ),
          )}
        </div>
      )}

      <div className="border-t border-border p-3">
        {/* Composer toolbar: extended-thinking opt-in (Claude-style), off by
            default. Persisted to localStorage so the choice sticks. */}
        <div className="mb-2 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setThinking((v) => !v)}
            title="Pensiero esteso: pianificazione più approfondita (più lento, più token)"
            aria-pressed={thinking}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${
              thinking
                ? "border-accent bg-accent/10 text-accent"
                : "border-border text-muted-foreground hover:bg-surface hover:text-foreground"
            }`}
          >
            <Sparkles className="h-3.5 w-3.5" strokeWidth={1.5} />
            Pensiero esteso
          </button>
        </div>
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
            rows={1}
            placeholder="Scrivi un messaggio… (Invio per inviare, Shift+Invio per andare a capo)"
            className="max-h-40 flex-1 resize-none overflow-y-auto rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/30"
          />
          {busy ? (
            <button
              type="button"
              onClick={cancel}
              className="flex h-9 items-center gap-1.5 rounded-md border border-border px-3 text-sm font-medium text-muted-foreground hover:bg-surface hover:text-foreground"
            >
              <Square className="h-3.5 w-3.5" strokeWidth={2} /> Annulla
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void send()}
              disabled={!input.trim()}
              className="flex h-9 items-center gap-1.5 rounded-md bg-accent px-3 text-sm font-medium text-accent-foreground disabled:opacity-50"
            >
              <Send className="h-4 w-4" /> Invia
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** A tool-activity chip in the turn stream: "🔧 <name> <status>" — keeps the
 *  agent's actions visually distinct from its prose (ADR-0024 S2). */
function ToolChip({ name, status }: { name: string; status: "running" | "ok" | "error" }) {
  const Status = status === "running" ? Loader2 : status === "ok" ? Check : X;
  const tone =
    status === "error" ? "text-danger" : status === "ok" ? "text-success" : "text-muted-foreground";
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-0.5 font-mono text-xs text-muted-foreground">
      <Wrench className="h-3 w-3" strokeWidth={1.5} />
      <span className="text-foreground">{name}</span>
      <Status
        className={`h-3 w-3 ${tone} ${status === "running" ? "animate-spin" : ""}`}
        strokeWidth={2}
      />
    </span>
  );
}

/** The human confirm gate (ADR-0024 B2): every destructive Odino op surfaces this
 *  card with TWO real buttons — Conferma (re-runs the tool confirmed) or Annulla
 *  (tells the agent to leave everything as is). Nothing is destroyed until the
 *  operator clicks. Once decided, the buttons collapse to a status line so the
 *  card can't be double-actioned. */
function ConfirmCard({
  message,
  items,
  busy,
  onConfirm,
  onCancel,
}: {
  message: string;
  items: string[];
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [decided, setDecided] = useState<null | "confirmed" | "cancelled">(null);
  const confirm = () => {
    setDecided("confirmed");
    onConfirm();
  };
  const cancel = () => {
    setDecided("cancelled");
    onCancel();
  };
  return (
    <div className="w-full rounded-md border border-danger/40 bg-danger/10 px-3 py-2">
      <div className="flex items-center gap-1.5 text-sm font-medium text-danger">
        <AlertTriangle className="h-4 w-4" strokeWidth={2} /> Conferma eliminazione
      </div>
      {items.length > 0 && (
        <ul className="mt-1 list-disc pl-5 text-xs text-foreground">
          {items.map((it, i) => (
            <li key={i} className="font-mono">
              {it}
            </li>
          ))}
        </ul>
      )}
      {message && <p className="mt-1 text-xs text-muted-foreground">{message}</p>}
      {decided === null ? (
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            onClick={confirm}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-danger px-3 py-1 text-xs font-medium text-danger-foreground hover:opacity-90 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" /> Conferma ed esegui
          </button>
          <button
            type="button"
            onClick={cancel}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1 text-xs font-medium text-foreground hover:bg-elevated disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" /> Annulla
          </button>
        </div>
      ) : (
        <p className="mt-2 text-xs font-medium text-muted-foreground">
          {decided === "confirmed" ? "✓ Confermato" : "✗ Annullato"}
        </p>
      )}
    </div>
  );
}

/** Compact "x min fa" for the history list — coarse on purpose (no seconds). */
function relTime(iso: string | null): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1) return "ora";
  if (mins < 60) return `${mins} min fa`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} h fa`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days} g fa`;
  return new Date(t).toLocaleDateString();
}

/** The per-user conversation history (ADR-0024): the operator's own sessions,
 *  most-recent first — click to reopen, trash to delete. Lives as a dropdown
 *  panel above the transcript (like the settings card). */
function HistoryPanel({
  sessions,
  activeId,
  onReopen,
  onDelete,
}: {
  sessions: OdinoSessionSummary[] | null;
  activeId: string | null;
  onReopen: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const [query, setQuery] = useState("");
  const groups =
    sessions === null ? null : groupSessionsByDate(filterSessions(sessions, query), Date.now());

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-background">
      {/* Sticky search bar */}
      <div className="border-b border-border p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca nelle conversazioni…"
            className="w-full rounded-md border border-border bg-surface py-2 pl-8 pr-3 text-sm outline-none focus:ring-2 focus:ring-accent/30"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {sessions === null ? (
          <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Carico la cronologia…
          </div>
        ) : (groups ?? []).length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">
            {query
              ? "Nessuna conversazione corrisponde alla ricerca."
              : "Nessuna conversazione ancora. Le tue chat con Odino compariranno qui."}
          </p>
        ) : (
          (groups ?? []).map((group) => (
            <div key={group.label}>
              <div className="sticky top-0 z-10 bg-background/95 px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur">
                {group.label}
              </div>
              <ul>
                {group.items.map((s) => (
                  <li key={s.id}>
                    <div
                      className={`group mx-2 flex items-center gap-2 rounded-md px-2 py-2 hover:bg-elevated ${
                        s.id === activeId ? "bg-elevated" : ""
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => onReopen(s.id)}
                        className="flex min-w-0 flex-1 flex-col items-start text-left"
                      >
                        <span className="w-full truncate text-sm text-foreground">
                          {s.title || "Senza titolo"}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {relTime(s.updated_at)} · {s.message_count} messaggi
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(s.id)}
                        title="Elimina conversazione"
                        aria-label="Elimina conversazione"
                        className="rounded-md p-1.5 text-muted-foreground opacity-0 hover:bg-danger/10 hover:text-danger group-hover:opacity-100"
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
